"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  completeWorkspaceIndustrySetup,
  getWorkspaceIndustrySetupContext,
  getGetWorkspaceIndustrySetupContextQueryKey,
} from "@/app/generated/api/preset/preset";
import { createWorkspace, selectWorkspace } from "@/app/generated/api/workspace/workspace";
import { getGetCurrentUserQueryKey } from "@/app/generated/api/auth/auth";
import type { CurrentUserResponse, WorkspaceDto } from "@/app/generated/api/schemas";
import type { IndustrySetupDriver, IndustrySetupInput } from "../onboarding/types";
import { invalidateWorkspaceApps } from "./invalidateWorkspaceApps";

export function useIndustrySetupContext(workspaceUuid: string, presetCode: string) {
  return useQuery({
    queryKey: getGetWorkspaceIndustrySetupContextQueryKey(workspaceUuid, presetCode),
    queryFn: () => getWorkspaceIndustrySetupContext(workspaceUuid, presetCode),
    enabled: workspaceUuid !== "",
    staleTime: 0,
  });
}

export function useCreateIndustryWorkspace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createWorkspace({ name }),
    onSuccess: (workspace) => {
      queryClient.setQueryData<CurrentUserResponse>(getGetCurrentUserQueryKey(), (current) => current ? {
        ...current,
        workspaces: [
          ...(Array.isArray(current.workspaces) ? current.workspaces as WorkspaceDto[] : [])
            .filter((item) => item.uuid !== workspace.uuid),
          workspace,
        ],
      } : current);
    },
  });
}

export function useSelectIndustryWorkspace() {
  return useMutation({ mutationFn: (workspaceUuid: string) => selectWorkspace(workspaceUuid) });
}

export function useCompleteIndustrySetup(workspaceUuid: string, driver: IndustrySetupDriver) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: IndustrySetupInput) => (
      completeWorkspaceIndustrySetup(workspaceUuid, driver.presetCode, driver.prepareInput(input))
    ),
    onSuccess: async (result) => {
      await invalidateWorkspaceApps(queryClient, workspaceUuid);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getGetWorkspaceIndustrySetupContextQueryKey(workspaceUuid, driver.presetCode),
        }),
        ...driver.invalidationKeys(workspaceUuid, result).map((queryKey) => (
          queryClient.invalidateQueries({ queryKey })
        )),
      ]);
    },
  });
}
